import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, CircularProgress, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import DocumentIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/DocumentIcon';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import { LabelSize } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import { TabsCDS } from 'app/shared/componentsRestyled/coreDesignSystem/TabsCDS';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { EXTRACTION_REQUEST_INCLUDES } from 'constants/documents';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { checkIsTitlePortalUpload } from 'utils/documents';
import { logger } from 'utils/logger';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getDocumentByIdAction } from 'store/documents/actions';
import {
  getDocumentExtractionFields,
  getDocuments,
  selectDocumentFilledExtractionFields,
  selectDocumentTotalLinkCount,
  selectFilledExtractionFieldsDocumentId,
} from 'store/documents/selectors';
import {
  selectDocumentTypeNameByDocumentId,
  selectInspectorExtractionFieldsByDocTypeId,
} from 'store/system/selectors';

import useDocumentUploaderName from '../hooks/useDocumentUploaderName';

import DocumentExtractionFieldsTab from './DocumentExtractionFieldsTab';
import {
  EXTRACTION_HELPER_BANNER_TEXT,
  EXTRACTION_PANEL_STATUS,
  EXTRACTION_STATUS_CHIP_CONFIG,
  getDetailRows,
  getDocumentHeaderSubtitle,
  getFileExtension,
  INSPECTOR_TABS_CONFIG,
  InspectorTab,
} from './DocumentInspector.constants';
import {
  collapseButtonStyles,
  countRowStyles,
  detailNameValueStyles,
  detailRowStyles,
  documentHeaderActionsStyles,
  documentHeaderNameRowStyles,
  documentHeaderStyles,
  documentHeaderSubtitleStyles,
  documentIconTileStyles,
  helperBannerErrorStyles,
  helperBannerWarningStyles,
  inspectorEmptyStyles,
  inspectorPaneStyles,
  statusChipDotStyles,
  statusRowStyles,
  tabContentStyles,
  tabsRowStyles,
} from './DocumentInspector.styles';
import { DocumentInspectorTestIds } from './DocumentInspector.testIds';
import {
  countBelowThresholdResults,
  countExtractedFieldResults,
  getExtractionCountText,
  getExtractionFieldLeaves,
  getExtractionPanelStatus,
  getExtractionStatusMeta,
} from './DocumentInspector.utils';
import DocumentLinksTab from './DocumentLinksTab';
import { getTaskLinkCount } from './DocumentLinksTab/DocumentLinksTab.utils';

const DocumentInspector = ({
  documentId,
  isExtractedFieldsViewNeeded,
  onShowFieldSource = null,
  onCollapse = null,
  activeTab: activeTabProp,
  onTabChange: onTabChangeProp,
}) => {
  const dispatch = useDispatch();

  // If parent lifts the tab state (passes activeTab + onTabChange), use those;
  // otherwise fall back to local state so the component is still usable standalone.
  const [localActiveTab, setLocalActiveTab] = useState(InspectorTab.AI_FIELDS);

  const activeTab = activeTabProp ?? localActiveTab;

  const documents = useSelector(getDocuments);
  const documentTypeName = useSelector(selectDocumentTypeNameByDocumentId(documentId));
  const extractionFields = useSelector(getDocumentExtractionFields(documentId));
  const totalLinkCount = useSelector(selectDocumentTotalLinkCount);
  const extractionFieldsResults = useSelector(selectDocumentFilledExtractionFields);
  const filledResultsDocumentId = useSelector(selectFilledExtractionFieldsDocumentId);

  const { isViewAllowed: isDocumentAssociationsViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_DOCUMENT_ASSOCIATIONS),
  );
  const { isViewAllowed: isExtractedFieldsViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_DOCUMENT_EXTRACTION_FIELDS),
  );

  const currentDocument = documents[documentId];
  const documentTypeId = currentDocument?.documentTypeId ?? '';

  const inspectorFlatFields = useSelector(
    selectInspectorExtractionFieldsByDocTypeId(documentTypeId),
  );

  const isAiFieldsTabVisible = isExtractedFieldsViewAllowed && isExtractedFieldsViewNeeded;
  const isLinksTabVisible = isDocumentAssociationsViewAllowed;

  // The extraction request WITH sessions, held in local state. The documents-list
  // payload carries a session-less request (sessions: []) and a list reload REPLACES
  // the store map — so deriving from the store alone can silently lose the sessions
  // and misreport needs-review (completed GenAI session) as never-ran. Local state
  // survives list reloads; onRefreshExtractionRequest re-hydrates after save/re-run.
  const [hydratedExtractionRequest, setHydratedExtractionRequest] = useState(null);

  const onRefreshExtractionRequest = useCallback(() => {
    if (!documentId) {
      return;
    }

    dispatch(
      getDocumentByIdAction({ documentId, include: EXTRACTION_REQUEST_INCLUDES, silent: true }),
    )
      .then(action => {
        const document = action?.payload;

        setHydratedExtractionRequest(
          document?.extractionRequest ?? document?.ExtractionRequest ?? null,
        );
      })
      .catch(err => {
        logger.error(err);
      });
  }, [dispatch, documentId]);

  useEffectSafeStrictMode(() => {
    if (!documentId || !isAiFieldsTabVisible) {
      return;
    }

    setHydratedExtractionRequest(null);
    onRefreshExtractionRequest();
  }, [documentId, isAiFieldsTabVisible, onRefreshExtractionRequest]);

  const extractionRequest =
    hydratedExtractionRequest ??
    currentDocument?.extractionRequest ??
    currentDocument?.ExtractionRequest ??
    null;

  const onTabChange = useCallback(
    (_, value) => {
      if (onTabChangeProp) {
        onTabChangeProp(value);

        return;
      }

      setLocalActiveTab(value);
    },
    [onTabChangeProp],
  );

  const fileExtension = useMemo(() => getFileExtension(currentDocument?.name), [currentDocument]);

  const uploaderName = useDocumentUploaderName({
    companyId: currentDocument?.companyId,
    userId: currentDocument?.createdByUserId,
    isEnabled: checkIsTitlePortalUpload(currentDocument?.UploadSource),
  });

  const detailRows = useMemo(
    () => getDetailRows({ document: currentDocument, documentTypeName, uploaderName }),
    [currentDocument, documentTypeName, uploaderName],
  );

  const headerSubtitle = useMemo(
    () =>
      getDocumentHeaderSubtitle({
        documentTypeName,
        createdDate: currentDocument?.createdDate,
        uploaderShortName: null,
      }),
    [currentDocument, documentTypeName],
  );

  const panelStatus = getExtractionPanelStatus(extractionRequest);
  const isResultsLoaded = filledResultsDocumentId === documentId;

  const definitionLeaves = useMemo(
    () => getExtractionFieldLeaves({ extractionFields, inspectorFlatFields }),
    [extractionFields, inspectorFlatFields],
  );

  const extractedCount = useMemo(() => {
    if (!isResultsLoaded) {
      return 0;
    }

    return countExtractedFieldResults({ leaves: definitionLeaves, extractionFieldsResults });
  }, [isResultsLoaded, definitionLeaves, extractionFieldsResults]);

  const statusChipConfig = panelStatus ? EXTRACTION_STATUS_CHIP_CONFIG[panelStatus] : null;
  const helperBannerText = panelStatus ? EXTRACTION_HELPER_BANNER_TEXT[panelStatus] : null;

  const statusMeta = getExtractionStatusMeta({
    extractionRequest,
    panelStatus,
    belowThresholdCount: isResultsLoaded ? countBelowThresholdResults(extractionFieldsResults) : 0,
  });

  const countText = getExtractionCountText({
    panelStatus,
    extractedCount,
    totalCount: definitionLeaves.length,
    isResultsLoaded,
  });

  if (!documentId) {
    return (
      <Box sx={inspectorPaneStyles}>
        {onCollapse && (
          <Box sx={documentHeaderActionsStyles}>
            <ButtonWithTooltip
              data-testid={DocumentInspectorTestIds.COLLAPSE_BUTTON}
              icon={ArrowCheckIcon}
              iconDirection={IconDirection.RIGHT}
              iconSize={IconSize[20]}
              size="small"
              sx={collapseButtonStyles}
              title="Collapse inspector"
              variant="text"
              onClick={onCollapse}
            />
          </Box>
        )}
        <Box sx={inspectorEmptyStyles}>
          <Typography color="text.secondary" variant="regular14">
            Select a document to inspect
          </Typography>
        </Box>
      </Box>
    );
  }

  // Tab badge mirrors the header's N (fields actually extracted), not the schema
  // size — the honest count. Hidden while running/loading (the header shows "—").
  const isTabCountKnown = isResultsLoaded && panelStatus !== EXTRACTION_PANEL_STATUS.PROCESSING;
  const aiFieldsCount = isTabCountKnown ? extractedCount : undefined;

  const visibleTabs = INSPECTOR_TABS_CONFIG.filter(tab => {
    if (tab.value === InspectorTab.AI_FIELDS) {
      return isAiFieldsTabVisible;
    }

    if (tab.value === InspectorTab.LINKS) {
      return isLinksTabVisible;
    }

    return true;
  }).map(tab => {
    if (tab.value === InspectorTab.AI_FIELDS) {
      return { ...tab, count: aiFieldsCount };
    }

    if (tab.value === InspectorTab.LINKS) {
      return { ...tab, count: totalLinkCount + getTaskLinkCount(currentDocument?.TaskId) };
    }

    return tab;
  });

  return (
    <Box sx={inspectorPaneStyles}>
      <Box sx={documentHeaderStyles}>
        <Box sx={documentHeaderNameRowStyles}>
          <Box sx={documentIconTileStyles}>
            {fileExtension ? (
              <Typography variant="bold12">{fileExtension}</Typography>
            ) : (
              <DocumentIcon size={IconSize[16]} />
            )}
          </Box>
          <Typography noWrap variant="bold14">
            {currentDocument?.name ?? documentTypeName}
          </Typography>
          {onCollapse && (
            <ButtonWithTooltip
              data-testid={DocumentInspectorTestIds.COLLAPSE_BUTTON}
              icon={ArrowCheckIcon}
              iconDirection={IconDirection.RIGHT}
              iconSize={IconSize[20]}
              size="small"
              sx={collapseButtonStyles}
              title="Collapse inspector"
              variant="text"
              onClick={onCollapse}
            />
          )}
        </Box>
        <Typography color="text.secondary" sx={documentHeaderSubtitleStyles} variant="regular12">
          {headerSubtitle}
        </Typography>
        {isAiFieldsTabVisible && statusChipConfig && (
          <Box data-testid={DocumentInspectorTestIds.STATUS_CHIP} sx={statusRowStyles}>
            <LabelCDS
              size={LabelSize.S}
              title={
                <>
                  {panelStatus === EXTRACTION_PANEL_STATUS.PROCESSING ? (
                    <CircularProgress color="inherit" size={11} />
                  ) : (
                    <Box component="span" sx={statusChipDotStyles} />
                  )}
                  {statusChipConfig.label}
                </>
              }
              variant={statusChipConfig.variant}
            />
          </Box>
        )}
        {isAiFieldsTabVisible && countText && (
          <Box data-testid={DocumentInspectorTestIds.COUNT_BADGE} sx={countRowStyles}>
            <Typography color="text.secondary" variant="regular12">
              <Typography color="text.primary" component="span" variant="bold12">
                {countText.primary}
              </Typography>
              {countText.rest}
            </Typography>
            {statusMeta && (
              <Typography color="text.disabled" variant="regular12">
                {statusMeta}
              </Typography>
            )}
          </Box>
        )}
        {isAiFieldsTabVisible && helperBannerText && (
          <Box
            data-testid={DocumentInspectorTestIds.HELPER_BANNER}
            sx={
              panelStatus === EXTRACTION_PANEL_STATUS.FAILED
                ? helperBannerErrorStyles
                : helperBannerWarningStyles
            }
          >
            <Typography variant="regular12">{helperBannerText}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={tabsRowStyles}>
        <TabsCDS
          data-testid={DocumentInspectorTestIds.TABS}
          tabs={visibleTabs}
          value={activeTab}
          onChange={onTabChange}
        />
      </Box>

      {activeTab === InspectorTab.AI_FIELDS && isAiFieldsTabVisible && (
        <Box sx={tabContentStyles}>
          <DocumentExtractionFieldsTab
            documentId={documentId}
            documentTypeId={documentTypeId}
            panelStatus={panelStatus}
            onRefreshExtractionRequest={onRefreshExtractionRequest}
            onShowFieldSource={onShowFieldSource}
          />
        </Box>
      )}

      {activeTab === InspectorTab.LINKS && isLinksTabVisible && (
        <Box sx={tabContentStyles}>
          <DocumentLinksTab
            definitionLeaves={definitionLeaves}
            documentId={documentId}
            documentTypeId={currentDocument?.documentTypeId ?? null}
            taskId={currentDocument?.TaskId ?? null}
          />
        </Box>
      )}

      {activeTab === InspectorTab.DETAILS && (
        <Box sx={tabContentStyles}>
          {detailRows.map(({ id, label, value, isFullWidth }) => (
            <Box key={id} sx={detailRowStyles}>
              <Typography color="text.secondary" variant="regular12">
                {label}
              </Typography>
              <Typography sx={isFullWidth ? detailNameValueStyles : undefined} variant="medium12">
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

DocumentInspector.propTypes = {
  documentId: PropTypes.string.isRequired,
  activeTab: PropTypes.string,
  isExtractedFieldsViewNeeded: PropTypes.bool,
  onCollapse: PropTypes.func,
  onShowFieldSource: PropTypes.func,
  onTabChange: PropTypes.func,
};

export default memo(DocumentInspector);
